import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Parents/players no longer pay FieldHouse directly - that's being worked out at
// the org level instead. Flip this back to true to restore mandatory subscription
// plan selection + Stripe checkout during activation without re-wiring anything.
const SUBSCRIPTION_SELECTION_ENABLED = false;

const ActivateAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    subscriptionPlanId: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(SUBSCRIPTION_SELECTION_ENABLED);
  const [plans, setPlans] = useState([]);
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({});
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid activation link');
      navigate('/login');
      return;
    }

    fetchUserInfo();
    if (SUBSCRIPTION_SELECTION_ENABLED) {
      fetchPlans();
    }
  }, [token]);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`/api/auth/activate/${token}`);
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Invalid or expired activation link');
        navigate('/login');
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      toast.error('Failed to load activation information');
      navigate('/login');
    } finally {
      setLoadingUser(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/subscriptions/plans');
      if (response.ok) {
        const data = await response.json();
        setPlans(data.filter(plan => plan.is_active));
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: '',
      });
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (SUBSCRIPTION_SELECTION_ENABLED && !formData.subscriptionPlanId) {
      newErrors.subscriptionPlanId = 'Please select a subscription plan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Activate account with password
      const activateResponse = await fetch('/api/auth/activate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password: formData.password,
        }),
      });

      if (!activateResponse.ok) {
        const error = await activateResponse.json();
        throw new Error(error.message || 'Failed to activate account');
      }

      const activateData = await activateResponse.json();

      // Store the token
      localStorage.setItem('token', activateData.token);

      if (SUBSCRIPTION_SELECTION_ENABLED) {
        // Then redirect to subscription checkout
        const checkoutResponse = await fetch('/api/subscriptions/checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${activateData.token}`
          },
          body: JSON.stringify({ planId: parseInt(formData.subscriptionPlanId) })
        });

        if (checkoutResponse.ok) {
          const checkoutData = await checkoutResponse.json();
          // Redirect to Stripe Checkout
          window.location.href = checkoutData.url;
        } else {
          // If checkout fails, still let them into the app but show a message
          const errorData = await checkoutResponse.json().catch(() => ({}));
          const errorMessage = errorData.message || errorData.error || 'Failed to start subscription checkout';
          console.error('Failed to create checkout session after activation:', errorMessage);

          // Show specific error message
          if (errorData.error === 'Stripe payments are not enabled') {
            toast.error('Stripe payments are not currently enabled. Please contact support for assistance.');
          } else {
            toast.error(`Account activated, but ${errorMessage.toLowerCase()}. Please select a plan from the subscription page.`);
          }
          navigate('/subscription');
        }
      } else {
        toast.success('Account activated!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error(error.message || 'Failed to activate account');
    } finally {
      setLoading(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading activation information...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo - Icon only, larger size */}
        <div className="flex items-center justify-center">
          <img 
            src="/fieldhouse-logo.png" 
            alt="FieldHouse" 
            className="h-32 w-auto"
          />
        </div>

        {/* Headline and Tagline */}
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Activate Your Account
          </h2>
          <p className="mt-2 text-sm text-gray-500 font-normal">
            Welcome to FieldHouse, {user.name}!
          </p>
        </div>
        
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Create Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`input-field pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Subscription Plan Selection - currently disabled, see SUBSCRIPTION_SELECTION_ENABLED */}
            {SUBSCRIPTION_SELECTION_ENABLED && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Subscription Plan <span className="text-red-500">*</span>
              </label>
              {loadingPlans ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading plans...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {plans.map((plan) => {
                    const isSelected = formData.subscriptionPlanId === plan.id.toString();
                    const features = Array.isArray(plan.features) ? plan.features : [];
                    
                    return (
                      <div
                        key={plan.id}
                        onClick={() => {
                          setFormData({ ...formData, subscriptionPlanId: plan.id.toString() });
                          if (errors.subscriptionPlanId) {
                            setErrors({ ...errors, subscriptionPlanId: '' });
                          }
                        }}
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                name="subscriptionPlanId"
                                value={plan.id}
                                checked={isSelected}
                                onChange={(e) => {
                                  setFormData({ ...formData, subscriptionPlanId: e.target.value });
                                  if (errors.subscriptionPlanId) {
                                    setErrors({ ...errors, subscriptionPlanId: '' });
                                  }
                                }}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                              />
                              <div className="ml-3 flex-1">
                                <div className="flex items-center justify-between">
                                  <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                                  <div className="text-right">
                                    <span className="text-2xl font-bold text-gray-900">
                                      {formatPrice(plan.amount)}
                                    </span>
                                    <span className="text-gray-600 text-sm">/{plan.interval}</span>
                                  </div>
                                </div>
                                {plan.description && (
                                  <p className="mt-1 text-sm text-gray-600">{plan.description}</p>
                                )}
                                {features.length > 0 && (
                                  <ul className="mt-3 space-y-1">
                                    {features.slice(0, 3).map((feature, index) => (
                                      <li key={index} className="flex items-start text-sm text-gray-700">
                                        <CheckIcon className="h-4 w-4 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                                        <span>{feature}</span>
                                      </li>
                                    ))}
                                    {features.length > 3 && (
                                      <li className="text-xs text-gray-500 mt-1">
                                        +{features.length - 3} more features
                                      </li>
                                    )}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {plans.length === 0 && !loadingPlans && (
                    <p className="text-sm text-gray-500">No subscription plans available.</p>
                  )}
                </div>
              )}
              {errors.subscriptionPlanId && (
                <p className="mt-1 text-sm text-red-600">{errors.subscriptionPlanId}</p>
              )}
            </div>
            )}
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading || loadingPlans}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                SUBSCRIPTION_SELECTION_ENABLED ? 'Activate Account & Subscribe' : 'Activate Account'
              )}
            </button>
            
            {/* Security reassurance */}
            <p className="text-center text-xs text-gray-500">
              🔒 Your information is secure and encrypted.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivateAccount;
